import BaseUI from "../base-ui.ts";
import LogoFragment from "../fragments/logo.ts";
import LobbyFragment from "../fragments/lobby.ts";
import Route from "../../enums/route.ts";
import State from "../../state/state.ts";
import EatOutApiClient from "../../api-client/eat-out-api-client.ts";
import UiUtils from "../../utils/ui-utils.ts";
import LogUtils from "../../utils/log-utils.ts";
import Group from "../../models/group.ts";
import MemberState from "../../enums/member-state.ts";
import V1GroupRestaurantCategoriesChosen from "../../payloads/incoming/v1-group-restaurant-categories-chosen.ts";
import V1UserGot from "../../payloads/incoming/v1-user-got.ts";
import V1GroupJoined from "../../payloads/incoming/v1-group-joined.ts";
import SharedApiLogic from "../../api-client/shared-api-logic.ts";
import V1GroupReadied from "../../payloads/incoming/v1-group-readied.ts";
import V1GroupTopRestaurants from "../../payloads/incoming/v1-group-top-restaurants.ts";
import GroupState from "../../enums/group-state.ts";
import StateUtils from "../../state/state-utils.ts";

export default class RestaurantCategoriesLobbyScreen extends BaseUI {
  private readonly lobby: LobbyFragment;

  constructor(
    state: State,
    apiClient: EatOutApiClient,
    navigateCallback: (route: Route) => void,
  ) {
    super("restaurant-categories-lobby-screen", state, apiClient, navigateCallback);

    // add UI elements
    this.lobby = new LobbyFragment(
      this.state,
      this.apiClient,
      navigateCallback,
    );

    // subscribe to events
    this.apiClient.v1GroupRestaurantCategoriesChosenCallback = this.handleV1GroupRestaurantCategoriesChosenCallback
      .bind(this);
    this.apiClient.v1GroupJoinedCallback = this.handleV1GroupJoined.bind(this);
    this.apiClient.v1GroupReadiedCallback = this.handleV1GroupReadied.bind(this);
    this.apiClient.v1UserGotCallback = this.handleV1UserGot.bind(this);
    this.apiClient.v1GroupTopRestaurantsCallback = this.handleV1GroupTopRestaurants.bind(this);

    // fill lobby
    this.fillLobbyWithNotReadyUsers();
  }

  public override render(newElements: HTMLElement[] = []): HTMLDivElement {
    // logo
    const logo: LogoFragment = new LogoFragment(this.state, this.apiClient, this.navigateCallback);
    newElements.push(logo.render());

    // group code
    const groupCode: string = StateUtils.getCurrentGroupCode(this.state);
    const tokenParagraph: HTMLParagraphElement = UiUtils.paragraph(`Code: ${groupCode}`);
    newElements.push(tokenParagraph);

    // "not ready" lobby label
    const isNotReadyParagraph: HTMLParagraphElement = UiUtils.paragraph(
      "Your friends sure are taking a while...",
    );
    newElements.push(isNotReadyParagraph);

    // "not ready" lobby
    newElements.push(this.lobby.render());

    return super.render(newElements);
  }

  private handleV1GroupJoined(v1GroupJoined: V1GroupJoined): void {
    SharedApiLogic.handleV1GroupJoined(this.state, v1GroupJoined);
  }

  private handleV1GroupReadied(v1GroupReadied: V1GroupReadied): void {
    SharedApiLogic.handleV1GroupReadied(this.state, v1GroupReadied);
  }

  private handleV1UserGot(v1UserGot: V1UserGot): void {
    this.lobby.addUser(v1UserGot.payload.user);
  }

  private handleV1GroupRestaurantCategoriesChosenCallback(
    v1GroupRestaurantCategoriesChosen: V1GroupRestaurantCategoriesChosen,
  ): void {
    SharedApiLogic.handleV1GroupRestaurantCategoriesChosenCallback(
      this.debug,
      this.state,
      v1GroupRestaurantCategoriesChosen,
    );

    this.lobby.removeUser(v1GroupRestaurantCategoriesChosen.payload.user_id);
  }

  private handleV1GroupTopRestaurants(v1GroupTopRestaurants: V1GroupTopRestaurants): void {
    // make sure event is related to this client's current group
    if (v1GroupTopRestaurants.payload.group_id !== this.state.groupState.currentGroupId) {
      LogUtils.error(
        `received V1GroupTopRestaurants payload for wrong group ID: '${v1GroupTopRestaurants.payload.group_id}'
          (current group ID: '${this.state.groupState.currentGroupId}')`,
      );
    }

    // save top restaurants (for lookup later)
    this.state.groupState.updateGroupTopChoices(
      v1GroupTopRestaurants.payload.group_id,
      v1GroupTopRestaurants.payload.top_restaurants,
    );

    // update group state
    this.state.groupState.updateGroupState(
      v1GroupTopRestaurants.payload.group_id,
      GroupState.RECEIVED_ALL_RESTAURANT_CATEGORY_PREFERENCES,
    );

    // now that we have the top restaurants, switch to the TopChoices screen
    this.navigateCallback(Route.TOP_CHOICES);
  }

  private fillLobbyWithNotReadyUsers(): void {
    // figure out which users are "Ready" or "Not Ready"
    const group: Group = StateUtils.getCurrentGroup(this.state);
    group.members.forEach((memberState: MemberState, userId: string): void => {
      switch (memberState) {
        case MemberState.JOINED: {
          // these users are "Not Ready"
          this.apiClient.sendGetUserPayload(userId);
          return;
        }
        case MemberState.SUBMITTED_RESTAURANT_CATEGORY_PREFERENCES: {
          // these users are "Ready"
          return;
        }
        default: {
          LogUtils.error(
            `attempted to render RestaurantCategoriesLobby screen, but user '${userId}' has an invalid MemberState: ${memberState}`,
          );
        }
      }
    });
  }
}
